import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as slices from "../../../employee/slices/employeeSlice";
import * as services from "../../../employee/services/employeeApi";
import * as service from "../../services/reportApi";
import { generateRandomString } from "utils/helper";
import dayjs from "dayjs";
import { setIsLoading } from "../../slices/reportSlice";

function usePND1KorReport() {
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const branchOptions = useSelector((state) => state.employee.branchOptions);
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const [showReport, setShowReport] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [search, setSearch] = useState({});
    const [searchValues, setSearchValues] = useState({});
    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const [pdfUrl, setPdfUrl] = useState(null);
    const [year, setYear] = useState(dayjs().year());

    const [isDownloadDoc, setIsDownloadDoc] = useState(false);
    const [isDownload, setIsDownload] = useState(false);
    const [isDownloadExcel, setIsDownloadExcel] = useState(false);
    const [isDownloadTxt, setIsDownloadTxt] = useState(false);
    const [isDownloadZip, setIsDownloadZip] = useState(false);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const useFetchOptions = (serviceFunction, companyId, groupType, dispatchFunction) => {
        const dispatch = useDispatch();
        useEffect(() => {
            const fetchData = async () => {
                dispatch(slices.setIsLoading(true));
                try {
                    const response = await serviceFunction({
                        requestId: generateRandomString(),
                        companyId,
                        groupType,
                    });
                    dispatch(dispatchFunction(response?.data));
                } catch (error) {
                    console.error('Failed to fetch data:', error);
                } finally {
                    dispatch(slices.setIsLoading(false));
                }
            };

            if (companyId) {
                fetchData();
            }
        }, [companyId, serviceFunction, groupType, dispatchFunction, dispatch]);
    };
    useFetchOptions(services.getBranch, currentCompany, "", slices.setBranchOptions);

    const modifiedOptions = (options) =>
        options?.map((opt) => ({
            ...opt,
            label: i18n.language === "en" ? (opt?.nameEn ? opt?.nameEn : opt.name) : opt.name,
            value: opt.id,
        }))?.sort((a, b) =>
            a.label.localeCompare(b.label, i18n.language === "en" ? "en" : "th", {
                sensitivity: "base",
            })
        );

    const modifiedBranchOptions = useMemo(() => modifiedOptions(branchOptions), [branchOptions, i18n.language]);

    const previewPND1KorReportPdf = useCallback(async (params = {}) => {
        try {
            dispatch(setIsLoading(true));

            const response = await service.ExportPND1KorReportPdf({
                requestId: generateRandomString(),
                companyId: currentCompany,
                ...pagination,
                isReport: "Y",
                sortBy: params.sortBy,
                year: params.year,
                branchId: params.branchId
            })

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            setPdfUrl(url);

        } catch (error) {
            alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, pagination])

    const exportPND1KorReportDocument = useCallback(
        async (params = {}, changeYear) => {
            setIsDownloadDoc(true);
            try {
                dispatch(setIsLoading(true));
                const res = await service.ExportPND1KorReportDocumentPdf({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "Cover-Page-PND1-kor.pdf");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownloadDoc(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const exportPND1KorReport = useCallback(
        async (params = {}, changeYear) => {
            setIsDownload(true);
            try {
                dispatch(setIsLoading(true));
                const res = await service.ExportPND1KorReportPdf({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "Attachment-PND1-kor.pdf");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownload(false);
            }
        },
        [currentCompany, dispatch, search]
    );
    const exportPND1KorReportExcel = useCallback(
        async (params = {}, changeYear) => {
            setIsDownloadExcel(true);
            try {
                dispatch(setIsLoading(true));
                const res = await service.exportPND1KorReportExcel({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "PND1-kor.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownloadExcel(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const exportPND1KorReportTxt = useCallback(
        async (params = {}, changeYear) => {
            setIsDownloadTxt(true);
            try {
                dispatch(setIsLoading(true));
                const res = await service.ExportPND1KorReportTxt({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                    month: params?.month?.format?.("MM") || params?.month
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "PND1Kor.txt");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownloadTxt(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const exportPND1KorReportZip = useCallback(
        async (params = {}, changeYear) => {
            setIsDownloadZip(true);
            try {
                dispatch(setIsLoading(true));
                const res = await service.ExportPND1KorReportZip({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    year: params.year,
                    month: params?.month?.format?.("MM") || params?.month
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "PND1K.zip");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownloadZip(false);
            }
        },
        [currentCompany, dispatch, search]
    );

    const handleSearch = (values) => {
        const selectedYear = values.year?.year() || dayjs().year();
        setShowReport(true)
        setSearchValues(values);
        setSearch({ year: selectedYear, branchId: values.branchId });
        previewPND1KorReportPdf({
            year: selectedYear,
            branchId: values.branchId
        });
    }

    return {
        isModalOpen,
        showReport,
        branchOptions: modifiedBranchOptions,
        pdfUrl,
        searchValues,
        isDownloadDoc,
        isDownload,
        isDownloadExcel,
        isDownloadTxt,
        isDownloadZip,
        setYear,
        handleSearch,
        setIsModalOpen,
        showModal,
        exportPND1KorReport,
        exportPND1KorReportDocument,
        exportPND1KorReportTxt,
        exportPND1KorReportZip,
        exportPND1KorReportExcel
    }
}

export default usePND1KorReport;